import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import BottleLabel from './BottleLabel';
import AddressForm, { formatAddress } from './AddressForm';
import { INDIAN_STATES } from '../constants/indianStates';
import { payWithRazorpay } from '../lib/razorpayCheckout';
import { api } from '../lib/api';
import { fbTrack, fbTrackPurchaseOnce } from '../lib/fbpixel';
import { toast } from '../hooks/use-toast';

function emptyCustomer(user) {
  return {
    name: user?.name || '',
    email: user?.email || '',
    contact: user?.phone || '',
    address: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
    label: 'Home',
    addressId: null,
  };
}

function fromSavedAddress(addr, user) {
  if (!addr) return emptyCustomer(user);
  return {
    name: addr.name || user?.name || '',
    email: user?.email || '',
    contact: addr.phone || user?.phone || '',
    address: addr.line1 || '',
    line2: addr.line2 || '',
    city: addr.city || '',
    state: addr.state || '',
    pincode: addr.pincode || '',
    label: addr.label || 'Home',
    addressId: addr.id,
  };
}

function validateCustomer(customer) {
  if (!customer.name.trim()) return 'Please enter the recipient name.';
  if (!/^[6-9]\d{9}$/.test(customer.contact.trim())) {
    return 'Enter a valid 10-digit Indian mobile number.';
  }
  if (!customer.address.trim() || customer.address.trim().length < 5) {
    return 'Delivery address is required.';
  }
  if (!customer.city.trim()) return 'City is required.';
  if (!customer.state.trim()) return 'State is required.';
  if (!/^\d{6}$/.test(customer.pincode.trim())) return 'Enter a valid 6-digit pincode.';
  return null;
}

export default function CartDrawer() {
  const navigate = useNavigate();
  const { items, open, setOpen, updateQty, removeItem, total, clear } = useCart();
  const { user, isLoggedIn, addresses, defaultAddress, setUser } = useAuth();
  const [step, setStep] = useState('cart');
  const [paying, setPaying] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [customer, setCustomer] = useState(() => emptyCustomer(null));

  const sortedAddresses = useMemo(() => {
    const list = [...(addresses || [])];
    list.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
    return list;
  }, [addresses]);

  useEffect(() => {
    if (!open) return;
    if (isLoggedIn && defaultAddress) {
      setSelectedAddressId(defaultAddress.id);
      setCustomer(fromSavedAddress(defaultAddress, user));
      setAddingAddress(false);
    } else if (isLoggedIn) {
      setSelectedAddressId(null);
      setCustomer(emptyCustomer(user));
      setAddingAddress(true);
    } else {
      setCustomer((c) => ({
        ...c,
        name: c.name || '',
        email: c.email || '',
        contact: c.contact || '',
      }));
      setAddingAddress(false);
    }
  }, [open, isLoggedIn, user, defaultAddress]);

  const close = () => {
    setOpen(false);
    setStep('cart');
    setAddingAddress(false);
  };

  const selectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setCustomer(fromSavedAddress(addr, user));
    setAddingAddress(false);
  };

  const applyNewAddress = (payload) => {
    setSelectedAddressId(null);
    setCustomer({
      name: payload.name,
      email: user?.email || customer.email,
      contact: payload.phone,
      address: payload.line1,
      line2: payload.line2,
      city: payload.city,
      state: payload.state,
      pincode: payload.pincode,
      label: payload.label,
      addressId: null,
    });
    setAddingAddress(false);
  };

  const onCheckout = async () => {
    if (addingAddress) {
      toast({
        title: 'Address required',
        description: 'Fill in your delivery address and tap Use this address.',
        variant: 'destructive',
      });
      return;
    }

    const problem = validateCustomer(customer);
    if (problem) {
      toast({ title: 'Incomplete details', description: problem, variant: 'destructive' });
      return;
    }
    if (!customer.email.trim() && !isLoggedIn) {
      toast({ title: 'Email required', description: 'Enter an email for the payment receipt.', variant: 'destructive' });
      return;
    }

    setPaying(true);
    try {
      const cartSnapshot = items.map((i) => ({
        id: i.id,
        name: i.name,
        size: i.size || 50,
        qty: i.qty,
        price: i.price,
        lineTotal: i.price * i.qty,
        slug: i.slug,
        shots: i.shots || [],
        image: i.shots?.[0] || '',
      }));
      const amountSnapshot = total;

      const result = await payWithRazorpay({
        items,
        customer: {
          name: customer.name.trim(),
          email: (customer.email || user?.email || '').trim(),
          contact: customer.contact.trim(),
        },
      });

      if (result.cancelled) {
        toast({ title: 'Payment cancelled', description: 'You can try again anytime.' });
        return;
      }

      if (result.ok) {
        // Fire before the /orders call so a failed order-sync still reports the
        // (already captured) payment. Deduped on paymentId.
        const paidValue =
          typeof result.amount === 'number' ? result.amount / 100 : amountSnapshot;
        fbTrackPurchaseOnce(result.paymentId || result.orderId, paidValue, {
          num_items: cartSnapshot.reduce((s, i) => s + i.qty, 0),
          content_ids: cartSnapshot.map((i) => i.id),
          content_type: 'product',
        });

        try {
          const saved = await api('/orders', {
            method: 'POST',
            body: {
              items: cartSnapshot,
              amount: amountSnapshot,
              paymentId: result.paymentId,
              razorpayOrderId: result.orderId,
              customer: {
                name: customer.name.trim(),
                email: (customer.email || user?.email || '').trim(),
                contact: customer.contact.trim(),
                address: customer.address.trim(),
                line2: customer.line2.trim(),
                city: customer.city.trim(),
                state: customer.state.trim(),
                pincode: customer.pincode.trim(),
                label: customer.label || 'Home',
                addressId: customer.addressId || undefined,
                saveAsDefault: true,
              },
            },
            auth: true,
          });
          if (saved.user) setUser(saved.user);
          clear();
          setStep('cart');
          setOpen(false);
          navigate(`/thank-you/${saved.order.id}`, {
            state: { order: saved.order, items: cartSnapshot },
            replace: true,
          });
        } catch (err) {
          clear();
          setStep('cart');
          setOpen(false);
          toast({
            title: 'Payment successful',
            description: err.message?.includes('address') || err.message?.includes('Address') || err.message?.includes('pincode')
              ? `${err.message} Payment ID: ${result.paymentId}`
              : `Paid (${result.paymentId}). Order sync pending — contact support with this ID if needed.`,
          });
        }
      }
    } catch (err) {
      toast({
        title: 'Checkout failed',
        description: err.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPaying(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 transition ${open ? 'visible pointer-events-auto' : 'invisible pointer-events-none'}`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={close}
      />
      <aside
        className={`absolute top-0 right-0 h-full w-full sm:w-[440px] bg-[#F5EFE6] shadow-2xl transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5DCC9]">
          <h3 className="font-serif-display text-xl">
            {step === 'details' ? 'Checkout' : 'Your Bag'}
          </h3>
          <button onClick={close} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'cart' ? (
          <>
            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100% - 200px)' }}>
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <p className="font-serif-body italic text-xl text-[#1A2332]/60">Your bag is empty.</p>
                  <button
                    onClick={close}
                    className="mt-6 px-6 py-3 bg-[#1A2332] text-[#F5EFE6] text-[11px] tracking-[0.25em] uppercase"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <ul className="space-y-5">
                  {items.map((it) => (
                    <li key={it.lineKey || it.id} className="flex gap-4 pb-5 border-b border-[#E5DCC9]">
                      <div
                        className="w-20 h-24 flex-shrink-0 flex items-center justify-center rounded-sm"
                        style={{
                          background: `radial-gradient(400px 200px at 50% 100%, ${it.accent}20, transparent 60%), linear-gradient(180deg, #FBF6EC 0%, #F0E6D3 100%)`,
                        }}
                      >
                        <BottleLabel product={it} size="sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <h4 className="font-serif-display text-[15px]">{it.name}</h4>
                          <button
                            onClick={() => removeItem(it.lineKey || it.id)}
                            className="text-[#1A2332]/50 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-[11px] tracking-[0.15em] uppercase text-[#1A2332]/60">
                          {it.size || 50} ML{it.code ? ` · ${it.code}` : ''}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-[#E5DCC9] rounded-sm">
                            <button onClick={() => updateQty(it.lineKey || it.id, it.qty - 1)} className="p-1.5">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center text-sm">{it.qty}</span>
                            <button onClick={() => updateQty(it.lineKey || it.id, it.qty + 1)} className="p-1.5">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="font-serif-display text-lg">₹{it.price * it.qty}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {items.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 border-t border-[#E5DCC9] bg-[#F5EFE6] p-6">
                <div className="flex justify-between mb-4">
                  <span className="text-sm tracking-wide uppercase">Subtotal</span>
                  <span className="font-serif-display text-xl">₹{total}</span>
                </div>
                <button
                  onClick={() => {
                    fbTrack('InitiateCheckout', {
                      value: total,
                      currency: 'INR',
                      num_items: items.reduce((s, i) => s + i.qty, 0),
                      content_ids: items.map((i) => i.id),
                      content_type: 'product',
                    });
                    setStep('details');
                  }}
                  className="w-full bg-[#1A2332] text-[#F5EFE6] py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#2c3e5b] transition"
                >
                  Checkout
                </button>
                <button
                  onClick={close}
                  className="w-full mt-2 text-[11px] tracking-[0.25em] uppercase underline underline-offset-4"
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="px-6 py-5 flex flex-col h-[calc(100%-73px)] overflow-y-auto">
            {!isLoggedIn && (
              <p className="text-xs text-[#1A2332]/70 mb-4 bg-white/60 border border-[#E5DCC9] p-3">
                <Link to="/login" onClick={close} className="underline">
                  Sign in
                </Link>{' '}
                to save addresses and track orders in your account.
              </p>
            )}
            <p className="text-sm text-[#1A2332]/70 mb-4">
              Pay securely with UPI, cards, or netbanking via Razorpay.
            </p>

            {isLoggedIn ? (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Deliver to</h4>
                  {!addingAddress && (
                    <button
                      type="button"
                      onClick={() => setAddingAddress(true)}
                      className="text-[10px] tracking-[0.15em] uppercase underline underline-offset-4"
                    >
                      {sortedAddresses.length ? 'Ship to new address' : 'Add address'}
                    </button>
                  )}
                </div>

                {sortedAddresses.length > 0 && !addingAddress && (
                  <ul className="space-y-2 mb-3">
                    {sortedAddresses.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() => selectAddress(a)}
                          className={`w-full text-left border px-3 py-3 transition ${
                            selectedAddressId === a.id
                              ? 'border-[#1A2332] bg-white'
                              : 'border-[#E5DCC9] bg-white/50 hover:border-[#C9A961]'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] tracking-[0.15em] uppercase text-[#8a6f2d]">{a.label}</span>
                            {a.isDefault && (
                              <span className="text-[9px] tracking-[0.12em] uppercase text-[#1A2332]/50">Default</span>
                            )}
                          </div>
                          <p className="text-sm font-medium">{a.name} · {a.phone}</p>
                          <p className="text-xs text-[#1A2332]/70 mt-0.5">{formatAddress(a)}</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {addingAddress && (
                  <div className="border border-[#E5DCC9] bg-white/70 p-3 mb-3">
                    <AddressForm
                      key="checkout-new"
                      defaults={{ name: user?.name || '', phone: user?.phone || '', isDefault: sortedAddresses.length === 0 }}
                      compact
                      showDefaultToggle={false}
                      submitLabel="Use this address"
                      onSubmit={applyNewAddress}
                      onCancel={sortedAddresses.length ? () => {
                        setAddingAddress(false);
                        if (defaultAddress) selectAddress(defaultAddress);
                      } : undefined}
                    />
                  </div>
                )}

                {!addingAddress && customer.address && (
                  <div className="text-xs text-[#1A2332]/60 mb-3">
                    Selected address is saved to your account after payment.
                  </div>
                )}
              </div>
            ) : (
              <>
                {[
                  ['name', 'Full name', 'text'],
                  ['contact', 'Mobile', 'tel'],
                  ['email', 'Email', 'email'],
                  ['address', 'Address', 'text'],
                  ['city', 'City', 'text'],
                ].map(([key, label, type]) => (
                  <label key={key} className="block mb-3">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">{label}</span>
                    <input
                      type={type}
                      value={customer[key]}
                      onChange={(e) => {
                        let v = e.target.value;
                        if (key === 'contact') v = v.replace(/\D/g, '').slice(0, 10);
                        setCustomer((c) => ({ ...c, [key]: v }));
                      }}
                      className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]"
                    />
                  </label>
                ))}
                <label className="block mb-3">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">State</span>
                  <select
                    required
                    value={customer.state}
                    onChange={(e) => setCustomer((c) => ({ ...c, state: e.target.value }))}
                    className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]"
                  >
                    <option value="" disabled>
                      Select state
                    </option>
                    {customer.state && !INDIAN_STATES.includes(customer.state) && (
                      <option value={customer.state}>{customer.state}</option>
                    )}
                    {INDIAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block mb-3">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Pincode</span>
                  <input
                    type="text"
                    value={customer.pincode}
                    onChange={(e) =>
                      setCustomer((c) => ({
                        ...c,
                        pincode: e.target.value.replace(/\D/g, '').slice(0, 6),
                      }))
                    }
                    className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]"
                    inputMode="numeric"
                  />
                </label>
              </>
            )}

            {isLoggedIn && !addingAddress && (
              <label className="block mb-3">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Email for receipt</span>
                <input
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                  className="mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]"
                />
              </label>
            )}

            <div className="mt-auto border-t border-[#E5DCC9] pt-5 pb-2 sticky bottom-0 bg-[#F5EFE6]">
              <div className="flex justify-between mb-4">
                <span className="text-sm tracking-wide uppercase">Pay now</span>
                <span className="font-serif-display text-xl">₹{total}</span>
              </div>
              <button
                onClick={onCheckout}
                disabled={paying || (isLoggedIn && addingAddress)}
                className="w-full bg-[#1A2332] text-[#F5EFE6] py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#2c3e5b] transition disabled:opacity-60"
              >
                {paying ? 'Opening Razorpay…' : 'Pay with Razorpay'}
              </button>
              <button
                onClick={() => setStep('cart')}
                disabled={paying}
                className="w-full mt-2 text-[11px] tracking-[0.25em] uppercase underline underline-offset-4"
              >
                Back to bag
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
